/**
 * Thin typed wrappers around the colony REST endpoints. No rendering logic
 * lives here — see colony-screen.ts for that. Keeping this file focused on
 * request/response shapes makes it easy to mock in colony-screen.test.ts.
 */
import { api } from '../../core/network/api';

export type ResourceType = 'REGOLITH' | 'WATER_ICE' | 'HE3' | 'COMPOSITE' | 'CRIMSON_DUST';

export type BuildingType =
  | 'COMMAND'
  | 'REGOLITH_MINE'
  | 'WATER_ICE_CONDENSER'
  | 'HE3_REACTOR'
  | 'COMPOSITE_FABRICATOR'
  | 'BARRACKS'
  | 'SCOUT_GARAGE'
  | 'BASTION_WALL'
  | 'SIEGE_WORKSHOP'
  | 'BUILDER_CAMP'
  | 'STRATEGIC_RESERVE';

export type UnitType =
  | 'LIGHT_INFANTRY'
  | 'DEFENCE_BUNKER'
  | 'ATTACK_ROVER'
  | 'ATTACK_GUNSHIP'
  | 'SCOUT_BIKE'
  | 'DEFENCE_TURRET_WALKER'
  | 'SIEGE_ARTILLERY'
  | 'SIEGE_MORTAR_TANK'
  | 'SETTLER_CONVOY';

export type UnitCategory = 'DEFENCE' | 'BASIC_ATTACK' | 'SCOUT' | 'BETTER_DEFENCE' | 'SIEGE' | 'SETTLER';

/** The four researchable groups — DEFENCE/BASIC_ATTACK are never gated by research, so they are not groups. */
export type ResearchGroup = 'SCOUT' | 'BETTER_DEFENCE' | 'SIEGE' | 'SETTLER';

export interface ResourceEntry {
  type: ResourceType;
  amount: number;
  ratePerHour: number;
  storageCap: number;
}

export interface ColonyView {
  id: string;
  name: string;
  tier: number;
  isBot: boolean;
  factionId: string;
  /** Team livery palette ids — see TeamColor / GET /me/colony/team-colors/palette. */
  primaryColor: string;
  secondaryColor: string;
  resources: ResourceEntry[];
  /** Points banked toward the current season. Reset on rollover. */
  seasonPoints: number;
  /** ISO timestamp this colony was founded. */
  createdAt: string;
}

/** One selectable team colour. The hex is server-supplied so the client keeps no palette copy of its own. */
export interface TeamColor {
  id: string;
  label: string;
  hex: string;
}

export interface TeamColorSelection {
  primaryColor: string;
  secondaryColor: string;
}

export interface BuildingView {
  type: BuildingType;
  level: number;
  constructing: boolean;
  constructionEndAt: string | null;
}

export interface RosterView {
  unitType: UnitType;
  level: number;
  quantityReserved: number;
  training: boolean;
  /** When the whole remaining batch finishes — used to pick the longest-remaining time-skip target. */
  trainEndAt: string | null;
  trainQuantity: number;
  /** When just the next single unit is delivered — units arrive one at a time, not all at once. */
  nextUnitAt: string | null;
  /** True when the unit's category is not yet unlocked via the tech tree. */
  locked: boolean;
  /** Crew required per unit — only Barracks-trained types count toward crew capacity. */
  crewSize: number;
}

/** Barracks training-slot and crew-accommodation status. Covers Barracks-trained units only — Scout Garage / Siege Workshop units are not crew-gated. */
export interface AccommodationView {
  concurrentTrainingSlotsUsed: number;
  concurrentTrainingSlotsMax: number;
  crewUsed: number;
  crewCapacity: number;
}

/** Colony-wide construction concurrency, gated by the Builder Camp's level. */
export interface ConstructionCapacityView {
  concurrentConstructionUsed: number;
  concurrentConstructionMax: number;
}

export interface TechView {
  /** The group currently being researched, or null once every group is complete. */
  currentGroup: ResearchGroup | null;
  /** Steps completed within currentGroup (0-indexed position of the step about to run/running). */
  currentStep: number;
  /** Derived 0-10 linear progress across the whole ladder — what building gates compare against. */
  totalStepsCompleted: number;
  researching: boolean;
  researchEndAt: string | null;
  /** When the in-flight research began — lets the client show elapsed-of-total, not just a countdown. Null when not researching. */
  researchStartAt: string | null;
  unlockedCategories: UnitCategory[];
}

export function fetchColony(): Promise<ColonyView> {
  return api.get<ColonyView>('/me/colony');
}

/**
 * Every live colony the caller owns, oldest first — their portfolio.
 * `GET /me/colony/all` has existed since multi-colony portfolios landed; the
 * profile's Colonies list is its first frontend consumer.
 */
export function fetchAllColonies(): Promise<ColonyView[]> {
  return api.get<ColonyView[]>('/me/colony/all');
}

export interface CreateColonyInput {
  worldId: string;
  name: string;
  primaryColor?: string;
  secondaryColor?: string;
}

/** POST /me/colony — founds the caller's first colony in `worldId`. Rejects if they already have one elsewhere, or the world is full/closed. */
export function createColony(input: CreateColonyInput): Promise<ColonyView> {
  return api.post<ColonyView>('/me/colony', input);
}

export function fetchBuildings(): Promise<BuildingView[]> {
  return api.get<BuildingView[]>('/me/buildings');
}

export function fetchRoster(): Promise<RosterView[]> {
  return api.get<RosterView[]>('/me/roster');
}

export function fetchTech(): Promise<TechView> {
  return api.get<TechView>('/me/tech');
}

export function fetchTeamColorPalette(): Promise<TeamColor[]> {
  return api.get<TeamColor[]>('/me/colony/team-colors/palette');
}

export function fetchTeamColors(): Promise<TeamColorSelection> {
  return api.get<TeamColorSelection>('/me/colony/team-colors');
}

export function updateTeamColors(selection: TeamColorSelection): Promise<TeamColorSelection> {
  return api.patch<TeamColorSelection>('/me/colony/team-colors', selection);
}

export function fetchAccommodation(): Promise<AccommodationView> {
  return api.get<AccommodationView>('/me/roster/accommodation');
}

export function fetchConstructionCapacity(): Promise<ConstructionCapacityView> {
  return api.get<ConstructionCapacityView>('/me/buildings/capacity');
}

export function constructBuilding(type: BuildingType): Promise<BuildingView> {
  return api.post<BuildingView>(`/me/buildings/${type}/construct`, {});
}

export function trainUnit(unitType: UnitType, quantity: number): Promise<RosterView> {
  return api.post<RosterView>(`/me/roster/${unitType}/train`, { quantity });
}

/** Cancels an in-progress training batch, refunding its full resource cost. */
export function cancelTraining(unitType: UnitType): Promise<RosterView> {
  return api.post<RosterView>(`/me/roster/${unitType}/cancel-train`, {});
}

export function levelUpUnit(unitType: UnitType): Promise<RosterView> {
  return api.post<RosterView>(`/me/roster/${unitType}/level-up`, {});
}

export function researchTech(): Promise<TechView> {
  return api.post<TechView>('/me/tech/research', {});
}

/** MASTER testing control only — the backend 403s outside development/test regardless of who calls this. */
export function debugAdjustBuildingLevel(type: BuildingType, delta: 1 | -1): Promise<BuildingView> {
  return api.post<BuildingView>(`/me/buildings/${type}/debug-level`, { delta });
}
